import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { CONSTANTS, Constants } from '../../commons/injection.token';
import { ChatCommandOutbound, ChatCommandTypeEnum } from '../../models/chat/chat-command.model';
import { ChatMessage } from '../../models/chat/chat-message.model';
import {
  buildChatChannelRoom,
  buildChatPresenceRoom,
  buildChatUserRoom,
  CHAT_ACK_TIMEOUT_ERROR_KEY,
  CHAT_COMMAND_EVENT,
  CHAT_OFFLINE_ERROR_KEY,
  CHAT_SOCKET_EVENTS,
  CHAT_TYPING_EVENT,
  ChatAckData,
  ChatChannelUpdatedData,
  ChatErrorData,
  ChatFavoriteUpdatedData,
  ChatMessageDeletedData,
  ChatPinUpdatedData,
  ChatPresenceUpdatedData,
  ChatReactionUpdatedData,
  ChatReadUpdatedData,
  ChatSavedUpdatedData,
  ChatSessionEndedData,
  ChatSettingsUpdatedData,
  ChatStatusUpdatedData,
  ChatSurfaceUpdatedData,
  ChatTypingData,
  ChatUnreadUpdatedData,
} from '../../models/chat/chat-socket.model';
import { SocketService } from '../socket/socket.service';

/** How long a dispatched command waits for its `chat:ack` before erroring. */
export const CHAT_ACK_TIMEOUT_MS = 15_000;

/** Minimum interval between `typing: true` relays per channel. */
export const CHAT_TYPING_THROTTLE_MS = 3_000;

// Commands that persist a ChatMessage — these carry the client-generated
// idempotency key (`clientMessageId`) so server-side retries never duplicate.
const MESSAGE_PRODUCING_COMMANDS: ReadonlyArray<ChatCommandTypeEnum> = [
  ChatCommandTypeEnum.MESSAGE_SEND,
  ChatCommandTypeEnum.THREAD_REPLY,
  ChatCommandTypeEnum.FORWARD,
];

interface PendingAck {
  subject: ReplaySubject<ChatAckData>;
  timeout: ReturnType<typeof setTimeout> | null;
}

export interface ChatSendCommandOptions {
  /**
   * What to do when the socket is down. Defaults to the safe rule below; pass
   * it explicitly only to override that default.
   *
   * `true` — the command joins the in-order offline queue and its ack
   * observable stays open until a reconnect delivers it. The composer relies on
   * this: the message renders as "sending", the banner explains why, and
   * RF-33/AC-29 guarantee in-order delivery without duplicates.
   *
   * `false` — the command is refused immediately with `chat.errors.offline`.
   */
  queueWhenOffline?: boolean;
}

/**
 * Internal Chat socket wrapper (SPEC-2026-07-internal-chat, T-16) — the
 * AttendanceCopilotSocketService precedent, richer:
 *
 * - room joins (`chat_channel_/chat_user_/chat_presence_` — validated
 *   server-side; refusals arrive on `chat:error`);
 * - one typed `listen$` stream per backend socket event;
 * - `sendCommand()` with requestId/clientMessageId generation, an in-order
 *   OFFLINE QUEUE flushed on reconnect (idempotent server-side via
 *   clientMessageId) and `chat:ack` correlation (15 s timeout → error);
 * - `onReconnect$` for the app to fetch the `/messages?after=` delta;
 * - `typing()` throttled to 1 relay / 3 s per channel.
 */
@Injectable({ providedIn: 'root' })
export class ChatSocketService implements OnDestroy {

  private readonly destroy$ = new Subject<void>();

  /** FIFO of commands accepted while disconnected — resent in order on reconnect. */
  private pendingCommands: ChatCommandOutbound[] = [];

  /** requestId → pending ack correlation entry. */
  private readonly pendingAcks = new Map<string, PendingAck>();

  /** channelId → last `typing: true` relay timestamp. */
  private readonly lastTypingSentAt = new Map<string, number>();

  private hasConnectedOnce = false;

  private readonly reconnectedSubject = new Subject<void>();

  /**
   * Emits on every RE-connection (never on the first connect). The app should
   * fetch the `GET /channels/:id/messages?after={cursor}` delta here — queued
   * commands are flushed automatically before this emits.
   */
  readonly onReconnect$: Observable<void> = this.reconnectedSubject.asObservable();

  // Typed streams — one per backend socket event (names from the chat
  // DeliveryService / channel / reaction / presence / settings services).
  readonly messageNew$ = this.listen$<ChatMessage>(CHAT_SOCKET_EVENTS.messageNew);
  readonly messageUpdated$ = this.listen$<ChatMessage>(CHAT_SOCKET_EVENTS.messageUpdated);
  readonly messageDeleted$ = this.listen$<ChatMessageDeletedData>(CHAT_SOCKET_EVENTS.messageDeleted);
  readonly reactionUpdated$ = this.listen$<ChatReactionUpdatedData>(CHAT_SOCKET_EVENTS.reactionUpdated);
  readonly unreadUpdated$ = this.listen$<ChatUnreadUpdatedData>(CHAT_SOCKET_EVENTS.unreadUpdated);
  readonly readUpdated$ = this.listen$<ChatReadUpdatedData>(CHAT_SOCKET_EVENTS.readUpdated);
  readonly ack$ = this.listen$<ChatAckData>(CHAT_SOCKET_EVENTS.ack);
  readonly presenceUpdated$ = this.listen$<ChatPresenceUpdatedData>(CHAT_SOCKET_EVENTS.presenceUpdated);
  readonly statusUpdated$ = this.listen$<ChatStatusUpdatedData>(CHAT_SOCKET_EVENTS.statusUpdated);
  readonly channelUpdated$ = this.listen$<ChatChannelUpdatedData>(CHAT_SOCKET_EVENTS.channelUpdated);
  readonly pinUpdated$ = this.listen$<ChatPinUpdatedData>(CHAT_SOCKET_EVENTS.pinUpdated);
  readonly typing$ = this.listen$<ChatTypingData>(CHAT_SOCKET_EVENTS.typing);
  readonly error$ = this.listen$<ChatErrorData>(CHAT_SOCKET_EVENTS.error);

  // SPEC-2026-09-internal-chat-pwa — personal-room signals.
  /** Which of my surfaces are connected — the product mutes alerts while the chat app is. */
  readonly surfaceUpdated$ = this.listen$<ChatSurfaceUpdatedData>(CHAT_SOCKET_EVENTS.surfaceUpdated);
  /** The gateway is closing this socket: the token expired. */
  readonly sessionExpired$ = this.listen$<ChatSessionEndedData>(CHAT_SOCKET_EVENTS.sessionExpired);
  /** The gateway is closing this socket: the session was revoked (product sign-out). */
  readonly sessionRevoked$ = this.listen$<ChatSessionEndedData>(CHAT_SOCKET_EVENTS.sessionRevoked);
  /** Favourite sections changed on another connection of mine (RF-23). */
  readonly favoriteUpdated$ = this.listen$<ChatFavoriteUpdatedData>(CHAT_SOCKET_EVENTS.favoriteUpdated);
  /** A message was saved or unsaved on another connection of mine (RF-23). */
  readonly savedUpdated$ = this.listen$<ChatSavedUpdatedData>(CHAT_SOCKET_EVENTS.savedUpdated);
  /** A notification level or my preferences changed on another connection of mine (RF-23). */
  readonly settingsUpdated$ = this.listen$<ChatSettingsUpdatedData>(CHAT_SOCKET_EVENTS.settingsUpdated);

  constructor(
    private readonly socketService: SocketService,
    @Optional() @Inject(CONSTANTS) private readonly constants: Constants | null,
  ) {
    this.socketService.getConnectedStatus()
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((connected) => this.handleConnectionChange(connected));

    // Resolve/reject pending commands from the correlation events.
    this.ack$
      .pipe(takeUntil(this.destroy$))
      .subscribe((ack) => this.settleAck(ack));
    this.error$
      .pipe(takeUntil(this.destroy$))
      .subscribe((error) => this.settleGatewayError(error));
  }

  /**
   * Re-opens the connection keeping every joined room — after the gateway closed it
   * (expired or revoked session) and the token was refreshed.
   */
  reconnect(): void {
    this.socketService.reconnect();
  }

  // -------------------------------------------------------------------------
  // Rooms
  // -------------------------------------------------------------------------

  joinChannel(channelId: string): void {
    this.socketService.joinEvent(buildChatChannelRoom(this.getCompanyCode(), channelId));
  }

  /**
   * Stops re-joining the channel room on reconnect. Rooms are server-managed:
   * events for this room may still arrive until the socket reconnects, so
   * callers must also unsubscribe/filter their channel streams.
   */
  leaveChannel(channelId: string): void {
    this.socketService.leaveEvent(buildChatChannelRoom(this.getCompanyCode(), channelId));
  }

  /** Own personal room (acks, unread badges) — the gateway refuses any other user's. */
  joinPersonal(userId?: string): void {
    this.socketService.joinEvent(buildChatUserRoom(this.getCompanyCode(), userId ?? this.getCurrentUserId()));
  }

  /** Company-wide presence/status broadcast room. */
  joinPresence(): void {
    this.socketService.joinEvent(buildChatPresenceRoom(this.getCompanyCode()));
  }

  // -------------------------------------------------------------------------
  // Streams
  // -------------------------------------------------------------------------

  /** Generic typed stream for a chat socket event (see CHAT_SOCKET_EVENTS). */
  listen$<T>(socketEvent: string): Observable<T> {
    return this.socketService.listen(socketEvent) as Observable<T>;
  }

  // -------------------------------------------------------------------------
  // Commands
  // -------------------------------------------------------------------------

  /**
   * Dispatches a `chat:command` and returns the correlated ack observable:
   * `next` + `complete` with the `chat:ack` on success; `error` with a
   * ChatAckData when the backend acks an error, the gateway refuses the
   * command (`chat:error` with requestId) or no ack arrives within
   * CHAT_ACK_TIMEOUT_MS (errorKey = CHAT_ACK_TIMEOUT_ERROR_KEY).
   *
   * The command is dispatched IMMEDIATELY (fire-and-forget safe — subscribing
   * is optional). While the socket is disconnected, commands are queued in
   * order and resent on reconnect; the server dedupes message-producing
   * retries via the stamped `clientMessageId`.
   */
  sendCommand(
    type: ChatCommandTypeEnum,
    payload: Record<string, unknown> = {},
    options: ChatSendCommandOptions = {},
  ): Observable<ChatAckData> {
    const commandPayload: Record<string, unknown> = { ...payload };
    if (MESSAGE_PRODUCING_COMMANDS.includes(type) && !commandPayload['clientMessageId']) {
      commandPayload['clientMessageId'] = this.generateId();
    }

    const command: ChatCommandOutbound = {
      type,
      requestId: this.generateId(),
      sentAt: new Date().toISOString(),
      payload: commandPayload,
    };

    const pending: PendingAck = { subject: new ReplaySubject<ChatAckData>(1), timeout: null };
    this.pendingAcks.set(command.requestId, pending);

    // Default: only message-producing commands are queued while offline. They
    // carry `clientMessageId`, so replaying them minutes later is idempotent and
    // the composer already renders the "sending"/offline state for them.
    //
    // Everything else (channel governance, favorites, status, settings, read
    // markers) fails fast with `chat.errors.offline`. A queued command's ack
    // observable never settles, and every one of those callers is a dialog or a
    // menu action that blocks on that ack — queueing them would leave a spinner
    // running forever with no explanation, which RNF-08 forbids.
    const queueWhenOffline = options.queueWhenOffline ?? MESSAGE_PRODUCING_COMMANDS.includes(type);
    this.dispatch(command, queueWhenOffline);

    return pending.subject.asObservable();
  }

  /**
   * Ephemeral typing relay — `typing: true` is throttled to one relay per
   * channel per CHAT_TYPING_THROTTLE_MS; `typing: false` always goes through
   * (and resets the throttle) so "stopped typing" is never swallowed.
   */
  typing(channelId: string, typing: boolean, userName?: string): void {
    if (!channelId) return;

    if (typing) {
      const now = Date.now();
      const last = this.lastTypingSentAt.get(channelId) ?? 0;
      if (now - last < CHAT_TYPING_THROTTLE_MS) return;
      this.lastTypingSentAt.set(channelId, now);
    } else {
      this.lastTypingSentAt.delete(channelId);
    }

    this.socketService.emit(CHAT_TYPING_EVENT, { channelId, typing, userName });
  }

  ngOnDestroy(): void {
    this.pendingAcks.forEach((pending) => {
      if (pending.timeout) clearTimeout(pending.timeout);
      pending.subject.complete();
    });
    this.pendingAcks.clear();
    this.pendingCommands = [];
    this.destroy$.next();
    this.destroy$.complete();
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  private handleConnectionChange(connected: boolean): void {
    if (!connected) return;

    const isReconnect = this.hasConnectedOnce;
    this.hasConnectedOnce = true;
    this.flushPendingCommands();
    if (isReconnect) {
      this.reconnectedSubject.next();
    }
  }

  private dispatch(command: ChatCommandOutbound, queueWhenOffline: boolean): void {
    if (this.socketService.emit(CHAT_COMMAND_EVENT, command)) {
      this.armAckTimeout(command.requestId);
      return;
    }

    if (queueWhenOffline) {
      // Queued: the ack observable deliberately stays open until a reconnect
      // flushes it (RF-33). No timeout is armed — a queued command has not been
      // sent yet, so there is nothing to time out.
      this.pendingCommands.push(command);
      return;
    }

    this.settleAck({
      requestId: command.requestId,
      status: 'error',
      errorKey: CHAT_OFFLINE_ERROR_KEY,
    });
  }

  /** Resends queued commands preserving FIFO order; re-queues what still cannot go out. */
  private flushPendingCommands(): void {
    if (this.pendingCommands.length === 0) return;

    const queued = this.pendingCommands;
    this.pendingCommands = [];
    for (const command of queued) {
      this.dispatch(command, true);
    }
  }

  private armAckTimeout(requestId: string): void {
    const pending = this.pendingAcks.get(requestId);
    if (!pending || pending.timeout) return;

    pending.timeout = setTimeout(() => {
      const entry = this.pendingAcks.get(requestId);
      if (!entry) return;
      this.pendingAcks.delete(requestId);
      entry.subject.error(<ChatAckData>{
        requestId,
        status: 'error',
        errorKey: CHAT_ACK_TIMEOUT_ERROR_KEY,
      });
    }, CHAT_ACK_TIMEOUT_MS);
  }

  private settleAck(ack: ChatAckData): void {
    const pending = ack?.requestId ? this.pendingAcks.get(ack.requestId) : undefined;
    if (!pending) return;

    if (pending.timeout) clearTimeout(pending.timeout);
    this.pendingAcks.delete(ack.requestId);

    if (ack.status === 'success') {
      pending.subject.next(ack);
      pending.subject.complete();
    } else {
      pending.subject.error(ack);
    }
  }

  /** Gateway refusals (unauthorized/moduleDisabled/rateLimited) carry the requestId. */
  private settleGatewayError(error: ChatErrorData): void {
    if (!error?.requestId) return;
    this.settleAck({ requestId: error.requestId, status: 'error', errorKey: error.errorKey });
  }

  private generateId(): string {
    const cryptoObj: Crypto | undefined = globalThis.crypto;
    if (cryptoObj?.randomUUID) {
      return cryptoObj.randomUUID();
    }
    // RFC 4122 v4 fallback for non-secure contexts.
    const bytes = new Uint8Array(16);
    if (cryptoObj?.getRandomValues) {
      cryptoObj.getRandomValues(bytes);
    } else {
      for (let i = 0; i < bytes.length; i++) {
        bytes[i] = Math.floor(Math.random() * 256);
      }
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  /** Same storage AbstractComponent.getCurrentUserUser() reads. */
  private readStoredUser(): { _id?: string; company?: { code?: string } } | null {
    try {
      const storageKey = this.constants?.['currentUser'];
      if (!storageKey) return null;
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored)?.user ?? null : null;
    } catch {
      return null;
    }
  }

  /** Mirrors AbstractComponent.getCodeCompanyCurrentUser() for service context. */
  private getCompanyCode(): string {
    return this.readStoredUser()?.company?.code ?? '';
  }

  private getCurrentUserId(): string {
    return this.readStoredUser()?._id ?? '';
  }
}
